/**
 * License section: header, key input, and licensed-product cards.
 *
 * @package Give\Vendors\LiquidWeb\Harbor
 */
import { useMemo, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { KeyRound, Loader2, RefreshCw } from 'lucide-react';
import { SectionHeader } from '@/components/atoms/SectionHeader';
import { LicenseKeyInputSkeleton } from '@/components/atoms/LicenseKeyInputSkeleton';
import { LicenseKeyInput } from '@/components/molecules/LicenseKeyInput';
import { LicenseProductCard } from '@/components/molecules/LicenseProductCard';
import { PRODUCTS } from '@/data/products';
import { groupLicenseProducts } from '@/lib/group-license-products';
import type { LicenseProduct } from '@/types/api';
import type HarborError from '@/errors/harbor-error';
import { getHarborDataValue } from '@/lib/harbor-data';

interface LicenseSectionProps {
    licenseKey:      string | null;
    licenseProducts: LicenseProduct[];
    tierNameMap:     Record<string, string>;
    tierRankMap:     Record<string, number>;
    onRemove:        () => Promise<HarborError | null>;
    onRefresh:       () => Promise<void>;
    isRefreshing:    boolean;
    isLoading:       boolean;
    activationUrl:   string | null;
}

/**
 * Pulse-skeleton that mirrors LicenseProductCard's layout while the license
 * resolver is still in flight.
 */
function LicenseSectionSkeleton() {
    return (
        <div className="space-y-3">
            { PRODUCTS.map( ( p ) => (
                <div key={ p.slug } className="rounded-lg border bg-card px-3 py-2.5 animate-pulse">
                    <div className="flex items-center gap-2">
                        { /* logo */ }
                        <div className="w-6 h-6 rounded shrink-0 bg-muted" />
                        { /* product name */ }
                        <div className="h-3.5 flex-1 rounded bg-muted" />
                        { /* tier badge */ }
                        <div className="h-4 w-14 rounded-full shrink-0 bg-muted" />
                        { /* chevron */ }
                        <div className="w-3.5 h-3.5 rounded shrink-0 bg-muted" />
                    </div>
                </div>
            ) ) }
        </div>
    );
}

/**
 * @since 1.3.0   Read subscriptionsUrl through the getHarborDataValue helper.
 * @since 1.0.0
 */
export function LicenseSection( {
    licenseKey,
    licenseProducts,
    tierNameMap,
    tierRankMap,
    onRemove,
    onRefresh,
    isRefreshing,
    isLoading,
    activationUrl,
}: LicenseSectionProps ) {
    const [ isEditing, setIsEditing ] = useState( false );

    const hasLicense = licenseKey !== null;
    const manageUrl  = getHarborDataValue( 'subscriptionsUrl' );

    const handleRemove = async (): Promise<HarborError | null> => {
        const error = await onRemove();
        if ( ! error ) {
            setIsEditing( false );
        }
        return error;
    };

    const groupedProducts = useMemo(
        () => groupLicenseProducts( licenseProducts, tierRankMap ),
        [ licenseProducts, tierRankMap ],
    );

    return (
        <div className="space-y-3">
            <SectionHeader
                icon={ <KeyRound className="w-4 h-4 text-muted-foreground" /> }
                label={ __( 'License', '%TEXTDOMAIN%' ) }
                action={ (
                    <button
                        type="button"
                        onClick={ onRefresh }
                        disabled={ isRefreshing }
                        className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        { isRefreshing
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RefreshCw className="w-3 h-3" />
                        }
                        { isRefreshing
                            ? __( 'Refreshing...', '%TEXTDOMAIN%' )
                            : __( 'Refresh', '%TEXTDOMAIN%' )
                        }
                    </button>
                ) }
            />

            { isLoading ? (
                <>
                    <LicenseKeyInputSkeleton />
                    <LicenseSectionSkeleton />
                </>
            ) : (
                <>
                    <LicenseKeyInput
                        currentKey={ licenseKey }
                        isEditing={ isEditing }
                        onEdit={ () => setIsEditing( true ) }
                        onCancel={ () => setIsEditing( false ) }
                        onRemove={ handleRemove }
                        onSuccess={ () => setIsEditing( false ) }
                    />
                    { ! hasLicense && (
                        <p className="text-xs text-muted-foreground leading-relaxed mt-0 mb-0">
                            { __( 'Enter your license key to download and activate premium products.', '%TEXTDOMAIN%' ) }
                        </p>
                    ) }
                </>
            ) }

            { ! isLoading && hasLicense && groupedProducts.length > 0 && (
                <div className="space-y-3">
                    { groupedProducts.map( ( g ) => (
                        <LicenseProductCard
                            key={ `${ g.productSlug }:${ g.tiers.some( ( t ) => t.is_valid && t.activated_here ) }` }
                            productSlug={ g.productSlug }
                            productName={ g.productName }
                            tiers={ g.tiers }
                            tierNameMap={ tierNameMap }
                            activationUrl={ activationUrl }
                        />
                    ) ) }

                    { manageUrl && (
                        <p className="text-xs text-muted-foreground text-center mt-1 mb-0">
                            <a href={ manageUrl } target="_blank" rel="noopener noreferrer" className="underline hover:opacity-75">
                                { __( 'Manage license in Liquid Web', '%TEXTDOMAIN%' ) }
                            </a>
                        </p>
                    ) }
                </div>
            ) }
        </div>
    );
}
